import { initializeApp } from "firebase/app";
import { 
  getDatabase, 
  ref, 
  set, 
  push, 
  onValue, 
  get, 
  update, 
  remove,
  query,
  orderByChild,
  equalTo
} from "firebase/database";
import { 
  getAuth, 
  signInWithEmailAndPassword, 
  createUserWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  sendPasswordResetEmail,
  updateProfile
} from "firebase/auth";

// Your Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyAXjTuFtvNiXNvVZxIQdlZJ9r1Mi5O31GU",
  authDomain: "revenueepc.firebaseapp.com",
  databaseURL: "https://revenueepc-default-rtdb.asia-southeast1.firebasedatabase.app",
  projectId: "revenueepc",
  storageBucket: "revenueepc.firebasestorage.app",
  messagingSenderId: "530332264744",
  appId: "1:530332264744:web:6ac359602064fc5e0cc81d",
  measurementId: "G-JGL3J7HJV0"
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const database = getDatabase(app);
const auth = getAuth(app);

// Database paths
const PROJECTS_PATH = "projects";
const STAFF_PATH = "staff";

// Auth functions
export const authService = {
  // Sign in with email and password
  signIn: async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      return userCredential.user;
    } catch (error) {
      console.error("Sign in error:", error);
      throw error;
    }
  },

  // Sign up with email and password
  signUp: async (email, password, displayName) => {
    try {
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      if (displayName) {
        await updateProfile(userCredential.user, { displayName });
      }
      return userCredential.user;
    } catch (error) {
      console.error("Sign up error:", error);
      throw error;
    }
  },

  // Sign out
  signOut: async () => {
    try {
      await signOut(auth);
    } catch (error) {
      console.error("Sign out error:", error);
      throw error;
    }
  },

  // Get current user
  getCurrentUser: () => {
    return auth.currentUser;
  },

  // Subscribe to auth state changes
  onAuthStateChanged: (callback) => {
    return onAuthStateChanged(auth, callback);
  },

  // Send password reset email
  resetPassword: async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Password reset error:", error);
      throw error;
    }
  }
};

// RTDB Helper functions
export const dbService = {
  // Get all projects
  getProjects: async () => {
    const dbRef = ref(database, PROJECTS_PATH);
    const snapshot = await get(dbRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      return Object.keys(data).map(key => ({
        id: key,
        ...data[key]
      }));
    }
    return [];
  },

  // Get a single project by ID
  getProject: async (projectId) => {
    const dbRef = ref(database, `${PROJECTS_PATH}/${projectId}`);
    const snapshot = await get(dbRef);
    if (snapshot.exists()) {
      return { id: projectId, ...snapshot.val() };
    }
    return null;
  },

  // Add a new project
  addProject: async (projectData) => {
    const dbRef = ref(database, PROJECTS_PATH);
    const newRef = push(dbRef);
    await set(newRef, projectData);
    return { id: newRef.key, ...projectData };
  },
  // In firebase/config.js
addProjectWithId: async (projectId, projectData) => {
  const dbRef = ref(database, `${PROJECTS_PATH}/${projectId}`);
  await set(dbRef, projectData);
  return { id: projectId, ...projectData };
},

  // Update a project
  updateProject: async (projectId, data) => {
    const dbRef = ref(database, `${PROJECTS_PATH}/${projectId}`);
    await update(dbRef, data);
    return { id: projectId, ...data };
  },

  // Delete a project
  deleteProject: async (projectId) => {
    const dbRef = ref(database, `${PROJECTS_PATH}/${projectId}`);
    await remove(dbRef);
  },

  // Get projects by email
  getProjectsByEmail: async (email) => {
    const allProjects = await dbService.getProjects();
    return allProjects.filter(project => project.email === email);
  },

  // Get projects by staff
  getProjectsByStaff: async (staffName) => {
    const allProjects = await dbService.getProjects();
    return allProjects.filter(project => project.staff === staffName);
  },

  // Get projects by status
  getProjectsByStatus: async (status) => {
    const allProjects = await dbService.getProjects();
    return allProjects.filter(project => project.status === status);
  },

  // Get project by portal PIN
  getProjectByPin: async (pin) => {
    const allProjects = await dbService.getProjects();
    return allProjects.find(project => project.portalPin === pin);
  },

  // Subscribe to real-time updates
// In the subscribeProjects function, add a check to prevent infinite loops
subscribeProjects: (callback) => {
  const dbRef = ref(database, PROJECTS_PATH);
  return onValue(dbRef, (snapshot) => {
    if (snapshot.exists()) {
      const data = snapshot.val();
      const projects = Object.keys(data).map(key => ({
        id: key,
        ...data[key]
      }));
      callback(projects);
    } else {
      callback([]);
    }
  }, (error) => {
    console.error("Error in subscribeProjects:", error);
    callback([]);
  });
},

  // Subscribe to a single project
  subscribeProject: (projectId, callback) => {
    const dbRef = ref(database, `${PROJECTS_PATH}/${projectId}`);
    return onValue(dbRef, (snapshot) => {
      if (snapshot.exists()) {
        callback({ id: projectId, ...snapshot.val() });
      } else {
        callback(null);
      }
    });
  },

  // Get staff list
  getStaffList: async () => {
    const dbRef = ref(database, STAFF_PATH);
    const snapshot = await get(dbRef);
    if (snapshot.exists()) {
      const data = snapshot.val();
      if (Array.isArray(data)) {
        return data;
      }
      return Object.keys(data).map(key => data[key]);
    }
    return ["Rahul Sharma", "Priya Patel", "Anjali Singh", "Vikram Desai", "Neha Joshi"];
  },

  // Update staff list
  updateStaffList: async (staffList) => {
    const dbRef = ref(database, STAFF_PATH);
    await set(dbRef, staffList);
  },

  // Initialize database
  initializeDatabase: async () => {
    const projects = await dbService.getProjects();
    if (projects.length === 0) {
      console.log("Database is empty. Ready for new projects.");
    }
  }
};

// Export auth for use in other files
export { auth };

// Default export
export default database;